import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import IconButton from '@mui/material/IconButton';
import MuiPaper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/system/Box';
import styled from 'styled-components/macro';
import { spacing } from '@mui/system';
import FileOpen from '@mui/icons-material/FileOpenOutlined';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../services/contexts';
import {
  formatCurrency,
  formatDecimal,
} from '../../services/utils/format-numbers';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DataTable from '../../components/DataTable';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/InfoOutlined';
import LightTooltip from '../Users/components/CreateUser/LightTooltip';
import { Item } from './styles';
import api from '../../Middleware/api/api';
import {
  PAGE_SIZE,
  BillingDataForOrigin,
} from '../../services/utils/units-constants';
import BillStatusLabel from '../../components/BillStatusLabel';
import {
  TextField,
  Stack,
  Grid,
  Button,
  TableCell,
  Typography,
} from '@mui/material';

import LogoLoader from '../../components/LogoLoader';
import { Field, Form, Formik } from 'formik';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import LoadingButton from '../../components/core/LoadingButton';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import shadows from '@mui/material/styles/shadows';
import dayjs from 'dayjs';

const Feedback = lazy(() => import('../../components/DataTable/Feedback'));

const Paper = styled(MuiPaper)(spacing);

const months = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

// eslint-disable-next-line no-unused-vars
const dataTableTheme = (appTheme) =>
  createTheme({
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            maxHeight: 300,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            '&[data-colindex="2"], &[data-colindex="3"], &[data-colindex="4"], &[data-colindex="5"]':
              {
                '& > *': {
                  display: 'flex',
                  justifyContent: 'right',
                  textAlign: 'right',
                },
                '& .MuiButton-root': {
                  margin: 'auto',
                },
              },
          },
        },
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          root: {
            '&[data-colindex="2"], &[data-colindex="3"], &[data-colindex="4"], &[data-colindex="5"]':
              {
                '& > *': {
                  justifyContent: 'right',
                  textAlign: 'right',
                },
                '& .MuiButton-root': {
                  margin: 'auto',
                },
              },
          },
        },
      },
    },
  });

function getStartDate() {
  let today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const targetDate = new Date(year, month - 12, 1);
  return returnDate(targetDate);
}

function getEndDate() {
  let today = new Date();
  let minusDays = 0;
  let targetDate = new Date(today.getTime() - minusDays * 24 * 60 * 60 * 1000);
  return returnDate(targetDate);
}

function returnDate(date) {
  return (
    date.getFullYear() +
    '-' +
    date.toLocaleString('en-US', { month: '2-digit' }) +
    '-' +
    date.toLocaleString('en-US', { day: '2-digit' })
  );
}

export function EnhancedTable({
  billingType,
  contractKey,
  contractStatus,
  endCustomerId,
}) {
  const { t, i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [loadingPDF, setLoadingPDF] = useState([]);
  const [error, setError] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showErrorFeedback, setShowErrorFeedback] = useState(false);
  const { token } = useAuth();
  const [changeFlag, setChangeFlag] = useState(Date.now());
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const [operatorData, setOperatorData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);

  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());

  const [BTNSubmitIsSubmitting, setBTNSubmitIsSubmitting] = useState(false);

  const handleIConClick = (billId) => {
    setOpenTooltipId(billId);
  };
  const handleTooltipClose = () => {
    setOpenTooltipId(null);
  };
  const handleTooltipOpen = (billId) => {
    setOpenTooltipId(billId);
  };

  const renderPaymentIntents = (props) => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html:
            showstripeSepaPaymentIntentIdAndStripeBillPaymentStatus(props),
        }}
      />
    );
  };

  const getBillCollectionDate = (props) => {
    return props.billCollectionDate != null
      ? new Date(props.billCollectionDate).toLocaleDateString()
      : t('LBLBillNotYetSent');
  };
  const getStripePaymentIntent = (props) => {
    return props.stripeSepaPaymentIntentId != null
      ? props.stripeSepaPaymentIntentId
      : '';
  };
  const getStripePaymentStatus = (props) => {
    return props.stripeSepaBillPaymentStatus != null
      ? props.stripeSepaBillPaymentStatus
      : '';
  };

  function showstripeSepaPaymentIntentIdAndStripeBillPaymentStatus(props) {
    let billCollectionDateSection = '';
    billCollectionDateSection =
      t('LBLBillCollectionDate') + ' : ' + getBillCollectionDate(props);
    let paymentIntentSection = '';
    paymentIntentSection =
      '<br>' +
      t('stripeSepaPaymentIntentId') +
      ' : ' +
      getStripePaymentIntent(props);
    let paymentStatusSection = '';
    paymentStatusSection =
      '<br>' +
      t('stripeSepaPaymentStatus') +
      ' : ' +
      getStripePaymentStatus(props);
    return (
      billCollectionDateSection + paymentIntentSection + paymentStatusSection
    );
  }

  const getBills = async (seletedPageSize, currentPageVal) => {
    if (
      endCustomerId == null ||
      endCustomerId === '' ||
      endCustomerId === 'undefined'
    ) {
      setOperatorData([]);
      setBTNSubmitIsSubmitting(false);
    } else {
      const params = new URLSearchParams({
        startDate: startDate + ' 00:00',
        endDate: endDate + ' 23:59',
        endCustomerId: endCustomerId,
        origin: BillingDataForOrigin.CONTRACT_PAGE,
        page: currentPageVal,
        size: seletedPageSize,
      });
      setBTNSubmitIsSubmitting(true);
      api
        .fetchBills(token, params)
        .then((response) => {
          if (response?.data?.data == null) {
            setOperatorData([]);
            setBTNSubmitIsSubmitting(false);
          } else {
            setTotalRecords(response?.data?.data?.totalNumberOfRecords);
            setOperatorData(response?.data?.data?.response);
            setBTNSubmitIsSubmitting(false);
          }
        })
        .catch((error) => {
          setOperatorData([]);
          setBTNSubmitIsSubmitting(false);
        });
    }
  };
  const sendPdfBillEmail = (selected) => {
    if (token) {
      try {
        api.sendBillsPdfEmail(token, selected).then(
          () => {
            setShowFeedback(true);
          },
          () => {
            setShowErrorFeedback(true);
          }
        );
      } catch (exception) {
        setShowErrorFeedback(true);
      }
    }
  };

  const dateFiltersInitialValues = {
    startDate: startDate,
    endDate: endDate,
  };

  const dateFiltersValidationSchema = useMemo(
    () =>
      Yup.object({
        startDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .label(t('LBLStartDate'))
          .required(t('startDateRequired')),
        endDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .min(Yup.ref('startDate'), t('LBLENdgStart'))
          .label(t('LBLEndDate'))
          .required(t('LBLEndDateReq')),
      }),
    []
  );

  const handleStartDateChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setStartDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setStartDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setStartDate(newValue);
      setFieldValue('startDate', newValue);
      console.error('Invalid date input');
    }
  };

  const handleEndDateChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setEndDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setEndDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setEndDate(newValue);
      setFieldValue('endDate', newValue);
      console.error('Invalid EndDate input');
    }
  };

  const handleSubmit = (values) => {
    setStartDate(values.startDate);
    setEndDate(values.endDate);
    getBills(pageSize, currentPage);
  };

  useEffect(() => {
    getBills(pageSize, currentPage);
  }, [currentPage, pageSize, changeFlag]);
  const columns = useMemo(
    () => [
      {
        name: 'billNumber',
        label: t('billnum'),
        options: {
          filter: false,
          sort: true,
          display: billingType === 'WITH_BILLING',
        },
      },
      {
        name: 'startDate',
        key: 'startDate',
        label: t('LBLStartDate'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'right',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLStartDate')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="right">
              {value !== null ? format(new Date(value), 'dd.MM.yyyy') : ''}
            </Typography>
          ),
        },
      },

      {
        name: 'endDate',
        key: 'endDate',
        label: t('LBLEndDate'),
        options: {
          filter: false,
          customHeadRender: () => (
            <TableCell
              align="left"
              variant="head"
              sx={{
                textAlign: 'right',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLEndDate')}
            </TableCell>
          ),
          customBodyRender: (value) => (
            <Typography align="right">
              {value !== null ? format(new Date(value), 'dd.MM.yyyy') : ''}
            </Typography>
          ),
        },
      },

      {
        name: 'energyLoaded',
        label: t('LBLAmountCharged'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            let valueData = value;
            if (i18n.language === 'en') {
              valueData = valueData.replace(/,/g, '.');
              valueData = formatDecimal(valueData);
            }
            return (
              <Typography align="right" paddingRight={2}>
                {value !== null ? valueData + ' kWh' : ''}
              </Typography>
            );
          },
        },
      },
      {
        name: 'totalTaxedCosts',
        label: t('totalCost'),
        options: {
          filter: false,
          sort: false,
          display: billingType === 'WITH_BILLING',
        },
      },
      {
        name: 'billingStatus',
        label: t('billStatus'),
        options: {
          filter: false,
          sort: false,
          display: billingType === 'WITH_BILLING',
          customBodyRender: (value) => {
            return <BillStatusLabel textAlign={'center'} title={value} />;
          },
          customHeadRender: () => (
            <TableCell
              align="center"
              variant="head"
              sx={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              {t('billStatus')}
            </TableCell>
          ),
        },
      },
      {
        name: 'open',
        label: ' ',
        options: {
          filter: false,
          sort: false,
          display: billingType === 'WITH_BILLING',
        },
      },
      {
        name: 'details',
        label: ' ',
        options: {
          filter: false,
          sort: false,
          customBodyRender(value) {
            return (
              <>
                {value.billingStatus == 'NOT_APPLICABLE' &&
                billingType == 'NO_BILLING' ? null : billingType ==
                    'WITH_BILLING' && value.billingStatus == 'GENERATED' ? (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      type="submit"
                      onClick={() => {
                        sendPdfBillEmail([value]);
                      }}
                    >
                      {t('LBLButtonSendBill')}
                    </Button>
                  </>
                ) : value.paymentMethod == 'STRIPE_SEPA' ? (
                  <>
                    <Item>
                      <Typography fontWeight="fontWeightMedium">
                        <InfoIcon
                          onClick={() => handleIConClick(value.billId)}
                          color="action"
                          fontSize="small"
                          cl
                        />
                      </Typography>
                    </Item>

                    <LightTooltip
                      open={openTooltipId === value.billId}
                      onClose={handleTooltipClose}
                      placement="top-start"
                      title={renderPaymentIntents(value) || ''}
                      onOpen={() => handleTooltipOpen(value.billId)}
                    ></LightTooltip>
                  </>
                ) : null}
              </>
            );
          },
        },
      },
    ],
    [operatorData, billingType, contractKey, renderPaymentIntents]
  );

  const renderData = useMemo(() => {
    return operatorData
      ?.sort(
        (entry1, entry2) =>
          entry2.billGenerationDate - entry1.billGenerationDate
      )
      .map((entry) => {
        const loading = loadingPDF.includes(entry.billNumber);

        const {
          monthNumber,
          yearNumber,
          energyLoaded,
          taxedFixCosts,
          taxedVarCosts,
          totalTaxedCosts,
          billingStatus,
          ...billCost
        } = entry;
        entry['month'] = `${months[monthNumber - 1]} ${yearNumber}`;

        const newEntry = {
          ...billCost,
          details: entry,
          energyLoaded: `${formatDecimal(energyLoaded)}`,
          taxedFixCosts: formatCurrency(taxedFixCosts),
          taxedVarCosts: formatCurrency(taxedVarCosts),
          totalTaxedCosts:
            totalTaxedCosts !== null ? formatCurrency(totalTaxedCosts) : null,
          month: `${months[monthNumber - 1]} ${yearNumber}`,
          billingStatus: billingStatus,
          open:
            billingStatus !== null && billingStatus != 'NOT_APPLICABLE' ? (
              <Box position="relative">
                <IconButton
                  data-bill-number={entry.billNumber}
                  disabled={loading}
                  aria-label={`open bill ${entry.billNumber}`}
                  href={`${window._env_.REACT_APP_BOOKING_SERVICE_API}/bookings/pdf/${entry.billNumber}?verificationId=${entry.billId}`}
                  target="_blank"
                >
                  <FileOpen />
                </IconButton>
                {loading && (
                  <CircularProgress
                    size={38}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      zIndex: 1,
                    }}
                  />
                )}
              </Box>
            ) : null,
        };

        return newEntry;
      });
  }, [operatorData, loadingPDF]);

  return (
    <>
      <LogoLoader loading={BTNSubmitIsSubmitting} />
      <Grid container justifyContent="flex-end">
        <Grid
          item
          xs={12}
          xl={3}
          py={1}
          display="flex"
          justifyContent="flex-end"
        >
          <Formik
            initialValues={dateFiltersInitialValues}
            validationSchema={dateFiltersValidationSchema}
            validateOnMount={true}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form noValidate style={{ width: '100%' }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="flex-end"
                  width="100%"
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="de"
                  >
                    <DatePicker
                      label={t('LBLStartDate')}
                      PaperProps={{ sx: { boxShadow: shadows[3] } }}
                      value={values.startDate}
                      onChange={(newValue) => {
                        handleStartDateChange(newValue, setFieldValue);
                      }}
                      renderInput={(params) => (
                        <Field
                          component={TextField}
                          {...params}
                          required
                          name="startDate"
                          margin="none"
                          autoComplete="off"
                          helperText={errors.startDate}
                          error={errors.startDate}
                          fullWidth
                          sx={{
                            input: { fontSize: 15, height: '1em' },
                            minWidth: 180,
                          }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'TT.MM.JJJJ',
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="de"
                  >
                    <DatePicker
                      PaperProps={{ sx: { boxShadow: shadows[3] } }}
                      label={t('LBLEndDate')}
                      value={values.endDate}
                      onChange={(newValue) => {
                        handleEndDateChange(newValue, setFieldValue);
                      }}
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <Field
                          component={TextField}
                          {...params}
                          name="endDate"
                          margin="none"
                          autoComplete="off"
                          helperText={errors.endDate}
                          error={errors.endDate}
                          fullWidth
                          sx={{
                            input: { fontSize: 15, height: '1em' },
                            minWidth: 180,
                          }}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'TT.MM.JJJJ',
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                      px: 4,
                      py: 0.5,
                      fontSize: 15,
                      borderRadius: 1,
                      minWidth: 140,
                    }}
                    disabled={!!errors.startDate || !!errors.endDate}
                    //loading={BTNSubmitIsSubmitting}
                  >
                    {t('BTNSubmit')}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
      <Paper>
        <>
          <ThemeProvider
            theme={(appTheme) => ({
              ...appTheme,
              components: {
                ...appTheme.components,
                ...dataTableTheme(appTheme).components,
              },
            })}
          >
            <DataTable
              title={t('invoicesview')}
              data={renderData}
              columns={columns}
              resizable={true}
              options={{
                filter: false,
                textLabels: {
                  body: {
                    noMatch: t('noinvoices'),
                  },
                },
                selectableRows: 'none',
                currentPage: currentPage,
                totalRecords: totalRecords,
              }}
              getData={(seletedPageSize, currentPageVal) => {
                setCurrentPage(currentPageVal);
                setPageSize(seletedPageSize);
              }}
            />
          </ThemeProvider>
          <Suspense fallback={<div />}>
            <p>
              <Feedback
                open={error}
                title={t('noinfound')}
                severity="warning"
                message={t('nopdf')}
                handleClose={() => setError(false)}
              />
            </p>
          </Suspense>
          <Suspense fallback={<div />}>
            <p>
              <Feedback
                open={showFeedback}
                title={t('LBLSendBillPdfMailSuccess')}
                severity="success"
                message={
                  <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
                    {t('MSGSendBillPdfMailSuccess')}
                  </Typography>
                }
                handleClose={() => {
                  setShowFeedback(false);
                  setChangeFlag(new Date());
                }}
              />
            </p>
          </Suspense>

          <Suspense fallback={<div />}>
            <p>
              <Feedback
                open={showErrorFeedback}
                title={t('LBLSendBillPdfMailError')}
                severity="warning"
                message={
                  <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
                    {t('MSGSendBillPdfMailError')}
                  </Typography>
                }
                // severity="warning"
                handleClose={() => {
                  setShowErrorFeedback(false);
                }}
              />
            </p>
          </Suspense>
        </>
      </Paper>
    </>
  );
}
